import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../../../lib/utils'

export type SaButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type SaButtonSize = 'sm' | 'md' | 'lg'

export interface SaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: SaButtonVariant
  size?: SaButtonSize
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

const VARIANT_CLASSES: Record<SaButtonVariant, string> = {
  primary:
    'bg-sa-primary text-sa-primary-foreground hover:bg-sa-primary-hover border border-transparent',
  secondary:
    'bg-sa-secondary text-sa-secondary-foreground hover:bg-sa-secondary-hover border border-sa-border',
  ghost:
    'bg-transparent text-sa-foreground hover:bg-sa-hover border border-transparent',
  danger:
    'bg-sa-danger text-white hover:opacity-90 border border-transparent',
  link:
    'bg-transparent text-sa-primary hover:underline border border-transparent px-0',
}

const SIZE_CLASSES: Record<SaButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-10 px-5 text-sm gap-2',
}

export function SaButton ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: SaButtonProps) {
  const isDisabled = disabled || loading

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-sa-md font-medium transition-colors',
        'sa-focus-ring disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        leftIcon
      )}
      {children ? <span>{children}</span> : null}
      {!loading && rightIcon}
    </button>
  )
}
